import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AiOutputFormat,
  AnalysisAxisRule,
  AnalysisDisplayRules,
  FieldDef,
} from '@citizen-walk/shared-types';
import { contrastTextColor } from '@citizen-walk/shared-types';
import type { PostMinimal } from '../posts';
import { usePhotoUrl, type AiCategoryMastersResult } from '../posts';
import {
  expandAllToClassificationRows,
  expandRowsByDisplayedAxes,
  type ClassificationRow,
} from './classificationAdapter';
import {
  appendEmptyBucket,
  clsRowToPost,
  collectCustomAxes,
  DEFAULT_SCORE_FILTER,
  deriveAxisOptionsFromDataSettings,
  buildAiAxisOptionsWithPerspectives,
  isAnalyzedForPerspective,
  perspectiveAxisCodes,
  EMPTY_AXIS_CODE,
  extractDefaultAxis,
  extractRuleAxis,
  isDefaultAxisKey,
  isPostFieldAxisKey,
  mirrorRowToExtraFields,
  normalizeBucketMethod,
  orientPostFieldAxisGroups,
  postToCodes,
  postToMeanSplitCodes,
  readPostRank,
  readPostScore,
  sortCellPosts,
  type AxisGroup,
  type AxisKey,
  type MatrixSortField,
  type MatrixSortOrder,
  type PostFieldAxisGroup,
} from './matrixAxisModel';
import { resolvePerspectiveAxis, type AnalysisPerspective } from './analysisPerspectives';

/**
 * 分析アプリ「マトリクス」タブ (Sprint X3 minimum + X4 軸選択 dropdown 拡張 + Sprint AV M5 駆動)。
 * 旧 `MatrixView.tsx` 813 行の核機能を段階移植中。
 *
 * X3 で minimum:
 *   - emotion.major × category.major 固定軸グリッド
 *   - cell expand (hover → tap、reviewer audit 🔴 1+2 反映で keyboard a11y 対応)
 *
 * X4 で追加 (R-38 #4):
 *   - 軸選択 dropdown — 縦軸 / 横軸を以下 4 種から選択可能:
 *     - emotion.major / emotion.category (感情マスタ major or label)
 *     - category.major / category.categoryCd (カテゴリマスタ major or code)
 *   - 縦軸 / 横軸 swap button (旧と同 UX)
 *   - 軸ヘッダーは M2.colorMap 由来の chip 色 (動的解決)
 *
 * Sprint AV で追加 (M5 駆動化):
 *   - M5 `matrix.defaultVAxis` / `defaultHAxis` を初期 dropdown 値として採用
 *   - M5 `matrix.vAxisRule` / `hAxisRule` / `additionalAxisRules` が指定する axisKey を
 *     dropdown 選択肢として追加 (既存 4 種に追加)
 *   - **bucketMethod=`custom-ranges`** をサポート: extraFields 由来の連続値を
 *     `customRanges[]` の min/max でグルーピング (例: HappinessScore -3..-1 / 0 / 1..3)
 *
 * docs/105 残課題 (bucketMethod 6 種 + cls データソース) で追加:
 *   - **bucketMethod 6 種を完全実装** (旧 MatrixView L159-259 踏襲):
 *     - `auto` / `each-value`: 既存の master / postField group 抽出をそのまま使う (従来の処理)。
 *       ※ `each-value` は v2 `AnalysisAxisRule['bucketMethod']` union に無い (parser が drop)
 *       ため、実質 `auto` と同経路。defensive に同一扱い。
 *     - `mean-split`: extraFields[axisKey] 数値の平均で 2 bucket (`≥avg` / `<avg`) に分割。
 *     - `positive-negative`: 0 を境に正負 2 bucket (`≥0（正）` / `<0（負）`) に分割。
 *     - `by-name`: master 軸なら code を master row の major/category 名に解決して bucket、
 *       それ以外 (postField / extraFields) は値をそのままユニーク化して bucket。
 *     - `custom-ranges`: 既存 (extraFields の連続値を customRanges[] の min/max で分割)。
 *   - **cls データソース切替** (旧 L39/68-128/533-539 踏襲):
 *     - 「投稿単位 (post)」⇔「AI 認識物単位 (cls)」のトグルを追加。
 *     - cls 選択時は classificationAdapter.expandAllToClassificationRows で各投稿の
 *       classifications を 1 行 = 1 セル要素として展開し、軸値は extraFields に mirror した
 *       擬似 PostMinimal を matrix のデータソースにする (1 認識物 = 1 マス)。
 *
 * 後方互換:
 *   - M5 (`matrixRules`) を渡さない / 未定義の場合は従来の hardcoded 4 種で動作
 *   - bucketMethod 未対応値が来ても落ちない (auto fallback)
 *
 * 制約:
 *   - master 未配置 / 読込中の placeholder メッセージ
 *   - post.aiAnalysisStatus !== 'done' の post は集計対象外 (連続値軸 = extraFields 系 を除く;
 *     extraFields は AI 解析と独立に書込まれる可能性があるため status check を skip)
 *   - 旧 cell drill-down → AnalysisView 絞込連携は scope 外
 */

export interface AnalysisMatrixViewProps {
  /** docs/29 §8-1: 追加の AI 解析の観点 (軸の候補に観点ごとの AI 解析の軸を足す)。 */
  perspectives?: readonly AnalysisPerspective[];
  /** 既定の観点の表示名 (追加の観点があるとき軸のラベルに付ける)。 */
  defaultPerspectiveLabel?: string | null;
  posts: PostMinimal[];
  resolver: AiCategoryMastersResult;
  onSelectPost: (post: PostMinimal) => void;
  /** Sprint AV: M5 matrix rules (未指定なら hardcoded 4 軸のみ)。 */
  matrixRules?: AnalysisDisplayRules['matrix'];
  /**
   * Sprint BG-DataSettings 18 (GAP U-L18 close): M3 出力フォーマット (useOutputFormat 経由)。
   * 指定されていれば ecwAdapter.extractEcwAxisCodes が format-driven で軸 code を解決する。
   * 未指定 (旧パス) では従来の `aiAnalysisEcw.emotions` / `categoryCodes` hardcode 経路を維持。
   */
  outputFormat?: AiOutputFormat | null;
  /**
   * Sprint BG-DataSettings 23: cell 展開時に「📊 これを分析」 button を出し、
   * セル内 post id を AnalysisPage に handoff (一覧タブに切替 + 一覧絞込)。
   * docs/107 Wave5: 分析(詳細)タブの card grid 既定軸に使うため、現在の縦軸/横軸キーも併せて渡す。
   */
  onAnalyzeCell?: (
    postIds: string[],
    context?: { vAxisKey: string; hAxisKey: string; dataSource: 'post' | 'cls' },
  ) => void;
  /**
   * Task C 動的化: 地区固有の DataSettings.postFields を軸候補として自動取込。
   * - rating-scale 型 → optionsMeta.min/max から **1 段階 = 1 バケット** (数値ごとのセル) を自動生成
   * - number 型 → 値域不明のため「全件」1 バケット
   * - select / multiselect 型 → optionsMeta.options (or field.options) から by-name bucket を生成
   * - 軸 key は `postField:<fieldKey>` prefix で master 由来 / M5 custom axis と衝突回避
   * - 未指定なら従来動作 (master + M5 custom のみ)
   */
  postFields?: FieldDef[];
}

// 軸エンジン (軸候補導出 / バケット化 / cls 展開 / sort) は matrixAxisModel.ts に集約済
// (分析画面 AnalysisListView と共有)。本ファイルはマトリクスタブの状態管理・描画のみを持つ。

/** Set の先頭要素だけを残す (Set は挿入順を保つので「最も当てはまる値」が残る)。 */
function firstCodeOnly(codes: Set<string>): Set<string> {
  if (codes.size <= 1) return codes;
  for (const c of codes) return new Set([c]);
  return codes;
}

/**
 * 軸 key から「分類セットの軸 key」を取り出す (例 `emotion.major` → `emotion`)。
 * master 由来の既定軸のみが分類セットの軸に対応するため、それ以外 (postField / M5 custom) は
 * 展開対象外として null を返す。
 */
function axisExpandRoot(axisKey: AxisKey): string | null {
  if (!isDefaultAxisKey(axisKey)) return null;
  const dot = axisKey.indexOf('.');
  return dot > 0 ? axisKey.slice(0, dot) : axisKey;
}

/**
 * scoreSizing ON 時のサムネサイズ (old2 scoreSizing 相当)。
 * |score| (0..3 想定) を 0.6〜1.4 倍に線形マップして base size を拡縮する。
 * score が無い post は base size のまま。clamp で 16〜160px に収める。
 */
function scoreSizedThumb(base: number, post: PostMinimal): number {
  const score = readPostScore(post);
  if (score === null) return base;
  const abs = Math.min(3, Math.abs(score));
  const factor = 0.6 + (abs / 3) * 0.8; // |0|→0.6, |3|→1.4
  return Math.round(Math.min(160, Math.max(12, base * factor)));
}

export function AnalysisMatrixView({
  perspectives,
  defaultPerspectiveLabel,
  posts,
  resolver,
  onSelectPost,
  matrixRules,
  outputFormat,
  onAnalyzeCell,
  postFields,
}: AnalysisMatrixViewProps) {
  // Sprint AV: M5 で custom axisKey が指定されていれば dropdown 候補に追加
  const customAxes = useMemo(() => collectCustomAxes(matrixRules), [matrixRules]);
  const customRuleByKey = useMemo(() => {
    const m = new Map<string, AnalysisAxisRule>();
    for (const c of customAxes) m.set(c.key, c.rule);
    return m;
  }, [customAxes]);

  // (2) 軸別「（該当なし）」表示: M5 の vAxisRule / hAxisRule / additionalAxisRules で
  //     includeEmpty=true が指定された axisKey の集合。既定軸 (root.level) にも適用できるよう、
  //     collectCustomAxes (既定軸 key を除外する) とは別に全 rule から直接 axisKey を集める。
  const includeEmptyByKey = useMemo(() => {
    const s = new Set<string>();
    const rules = [
      matrixRules?.vAxisRule,
      matrixRules?.hAxisRule,
      ...(matrixRules?.additionalAxisRules ?? []),
    ];
    for (const r of rules) if (r?.includeEmpty && r.axisKey) s.add(r.axisKey);
    return s;
  }, [matrixRules]);

  // Task C 動的化: dataSettings.postFields から軸候補を生成 (rating-scale / number / select /
  // multiselect)。`postField:<key>` prefix で master 由来軸と衝突回避。
  const postFieldAxes = useMemo(() => deriveAxisOptionsFromDataSettings(postFields), [postFields]);
  const postFieldAxisByKey = useMemo(() => {
    const m = new Map<string, { field: FieldDef; groups: PostFieldAxisGroup[] }>();
    for (const a of postFieldAxes) m.set(a.key, { field: a.field, groups: a.groups });
    return m;
  }, [postFieldAxes]);

  // docs/105: cls データソース。outputFormat.mode を expandAllToClassificationRows に渡し、
  // 各投稿の classifications を 1 行 = 1 セル要素として展開する。
  const classificationRows = useMemo(
    () => expandAllToClassificationRows(posts, outputFormat, outputFormat?.mode),
    [posts, outputFormat],
  );
  const hasClassifications = classificationRows.length > 0;
  // cls 切替トグル。classifications が 1 件も無ければ post に固定。
  const [dataSource, setDataSource] = useState<'post' | 'cls'>('post');
  // M5 matrixRules.dataSource='cls' を既定として尊重する (admin が地区ごとに初期ビューを選べる)。
  // classifications がロードされる前に matrix が mount されるケースがあるため、classifications が
  // 利用可能になった「最初の1回だけ」cls に切替える (以後はユーザー操作を優先)。
  const appliedClsDefaultRef = useRef(false);
  useEffect(() => {
    if (!appliedClsDefaultRef.current && hasClassifications && matrixRules?.dataSource === 'cls') {
      appliedClsDefaultRef.current = true;
      setDataSource('cls');
    }
  }, [hasClassifications, matrixRules?.dataSource]);
  // classifications が消えたら post に戻す (cls 選択中に投稿/format が変わるケース)。
  useEffect(() => {
    if (!hasClassifications && dataSource === 'cls') setDataSource('post');
  }, [hasClassifications, dataSource]);

  // cls 行を「擬似 PostMinimal」に変換 (旧 clsAsPosts 踏襲、v2 では extraFields に軸値を mirror)。
  // 各軸の code / name / majorCode を extraFields に展開し、既定 master 軸・postField 軸・rule 軸の
  // いずれの集計経路でも cls 1 行 = 1 マス要素として扱えるようにする。
  const clsNegativeLabel = outputFormat?.schema?.assessment?.negativeLabel;
  // (a) AI 認識物単位の各行に親投稿の入力項目 (KeikanType 等) を継承させるための postId→extraFields。
  const postExtraById = useMemo(() => {
    const m = new Map<string, Record<string, unknown>>();
    for (const p of posts) if (p.extraFields) m.set(p.id, p.extraFields);
    return m;
  }, [posts]);
  // (b) 投稿単位: 各投稿を「代表 = rank 最小の分類」の値で投影する。分類付随軸 (資源/課題=assessment
  //     等) を投稿単位でも第1位代表で集計できるよう、代表分類の値を extraFields にミラーする
  //     (投稿の入力項目は温存、collision 時は分類由来を優先=AI認識物単位と対称)。
  const repRowByPost = useMemo(() => {
    const m = new Map<string, ClassificationRow>();
    for (const row of classificationRows) {
      const cur = m.get(row.postId);
      if (!cur || Number(row.rank) < Number(cur.rank)) m.set(row.postId, row);
    }
    return m;
  }, [classificationRows]);
  const projectedPosts = useMemo<PostMinimal[]>(() => {
    if (repRowByPost.size === 0) return posts;
    return posts.map((p) => {
      const rep = repRowByPost.get(p.id);
      if (!rep) return p;
      return {
        ...p,
        extraFields: { ...(p.extraFields ?? {}), ...mirrorRowToExtraFields(rep, clsNegativeLabel) },
      };
    });
  }, [posts, repRowByPost, clsNegativeLabel]);

  const isClsMode = dataSource === 'cls' && hasClassifications;

  // Sprint BG-DataSettings 28: 動的 axis options。load 済 master の templateVar 由来 +
  // M5 matrixRules.additionalAxisRules による custom axis (extraFields 連動) +
  // Task C dataSettings.postFields 由来軸。
  const axisOptions = useMemo<{ key: AxisKey; label: string }[]>(() => {
    // docs/29 §8-1: 追加の観点があれば観点ごとの AI 解析の軸も並べる。
    const base = buildAiAxisOptionsWithPerspectives(
      resolver,
      perspectives,
      defaultPerspectiveLabel,
    );
    const all = [
      ...base,
      ...postFieldAxes.map((a) => ({ key: a.key, label: a.label })),
      ...customAxes.map((c) => ({ key: c.key, label: c.label })),
    ];
    // M5 matrix.hiddenAxisKeys: 地区が不要とした軸 (例: category.categoryCd) を選択肢から除外。
    const hidden = matrixRules?.hiddenAxisKeys;
    if (hidden && hidden.length > 0) {
      const hideSet = new Set(hidden);
      return all.filter((o) => !hideSet.has(o.key));
    }
    return all;
  }, [
    resolver,
    customAxes,
    postFieldAxes,
    matrixRules?.hiddenAxisKeys,
    perspectives,
    defaultPerspectiveLabel,
  ]);

  const axisLabelByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const opt of axisOptions) m.set(opt.key, opt.label);
    return m;
  }, [axisOptions]);

  const labelOf = (k: AxisKey) => axisLabelByKey.get(k) ?? customRuleByKey.get(k)?.axisKey ?? k;

  // Sprint AV: defaultVAxis / defaultHAxis を初期値に採用 (option に含まれる場合のみ)
  const initialV = useMemo<AxisKey>(() => {
    const candidate = matrixRules?.defaultVAxis;
    if (candidate && axisOptions.some((o) => o.key === candidate)) return candidate;
    return 'emotion.major';
  }, [matrixRules, axisOptions]);
  const initialH = useMemo<AxisKey>(() => {
    const candidate = matrixRules?.defaultHAxis;
    if (candidate && axisOptions.some((o) => o.key === candidate)) return candidate;
    return 'category.major';
  }, [matrixRules, axisOptions]);

  const [yAxisKey, setYAxisKey] = useState<AxisKey>(initialV);
  const [xAxisKey, setXAxisKey] = useState<AxisKey>(initialH);

  // (3) AI 認識物単位: 感情 / 対象コード / ゲール基準のように 1 投稿で複数を列挙する軸は、
  //     **縦軸・横軸に選ばれているものだけ** 値ごとの行へ展開する。代表値 (先頭) だけを見ると
  //     2 件目以降がどのセルにも現れず、逆に全軸を展開すると見えない軸で件数が水増しされる。
  const expandedClsRows = useMemo(
    () =>
      expandRowsByDisplayedAxes(classificationRows, [
        axisExpandRoot(yAxisKey),
        axisExpandRoot(xAxisKey),
      ]),
    [classificationRows, yAxisKey, xAxisKey],
  );
  const clsAsPosts = useMemo<PostMinimal[]>(
    () =>
      expandedClsRows.map((row) =>
        clsRowToPost(row, clsNegativeLabel, postExtraById.get(row.postId) ?? null),
      ),
    [expandedClsRows, clsNegativeLabel, postExtraById],
  );

  // matrix の実データソース (投稿単位 or AI 認識物単位)。
  const matrixPosts = isClsMode ? clsAsPosts : projectedPosts;

  // docs/107 Wave4 / old2 MatrixView L658-678: セル内サムネイルの画像サイズ (px)。
  // 既定値は M5 matrixRules.imageSize (admin 設定) を優先、未指定なら 40px。
  const initialImageSize = useMemo<number>(() => {
    const v = matrixRules?.imageSize;
    if (typeof v === 'number' && Number.isFinite(v)) return Math.min(96, Math.max(12, v));
    return 40;
  }, [matrixRules]);
  const [imageSize, setImageSize] = useState<number>(initialImageSize);

  // セル内の表示件数まわりの state:
  //  - expandedCells: 「+N」をタップして全件展開したセル key の集合 (要件1)
  //  - expandAllCells: 全セルの写真を常に全件表示するモード (要件2)
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [expandAllCells, setExpandAllCells] = useState(false);

  // docs/107 Wave4 (remaining) / old2 MatrixView L34-44,506-637: ⚙設定 popover の各設定。
  // 開閉 state。
  const [settingsOpen, setSettingsOpen] = useState(false);
  // スマホ対応: データソース/軸セレクタ行を既定で畳み、グリッドに画面を譲る (sm+ では常時展開)。
  // 現在の縦×横軸はヘッダタイトルに常時出るため、畳んでも何を表示中かは分かる。
  const [axisBarOpen, setAxisBarOpen] = useState(false);
  // セル内 post タイトル併記 (old2 showTitle、既定は matrixRules.showTitle)。
  const [showTitle, setShowTitle] = useState<boolean>(matrixRules?.showTitle ?? false);
  // スコア連動サイズ: |score| でサムネを拡縮 (old2 scoreSizing)。
  const [scoreSizing, setScoreSizing] = useState(false);
  // 縦横軸大型表示: 軸ヘッダーを大型フォントで強調 (old2 axisDisplayName 大型表示)。
  const [largeAxis, setLargeAxis] = useState(false);
  // セル内並び順 (old2 sortField/sortOrder、既定は matrixRules)。
  const initialSortField = useMemo<MatrixSortField>(() => {
    const f = matrixRules?.sortField;
    return f === 'score' || f === 'rank' || f === 'date' ? f : 'score';
  }, [matrixRules]);
  const initialSortOrder: MatrixSortOrder = matrixRules?.sortOrder === 'asc' ? 'asc' : 'desc';
  const [sortField, setSortField] = useState<MatrixSortField>(initialSortField);
  const [sortOrder, setSortOrder] = useState<MatrixSortOrder>(initialSortOrder);
  // スコアフィルタ: 表示する score 値集合 (old2 scoreFilter、既定は matrixRules.scoreFilter)。
  const initialScoreFilter = useMemo<Set<number>>(() => {
    const f = matrixRules?.scoreFilter;
    if (Array.isArray(f) && f.length > 0) return new Set(f);
    return new Set(DEFAULT_SCORE_FILTER);
  }, [matrixRules]);
  const [scoreFilter, setScoreFilter] = useState<Set<number>>(initialScoreFilter);
  // ランクフィルタ: cls モードで表示する AI 認識物ランク集合 (old2 rankFilter)。
  // 既定は cls 行に存在する全ランク (allRanks)。
  const allRanks = useMemo<number[]>(() => {
    const s = new Set<number>();
    for (const row of classificationRows) s.add(row.rank);
    return [...s].sort((a, b) => a - b);
  }, [classificationRows]);
  const [rankFilter, setRankFilter] = useState<Set<number> | null>(null);
  // allRanks が判明したら未設定の rankFilter を全選択で初期化。
  // ★ 本番不具合修正: 非同期ロード中 (classifications 未到着) は allRanks=[] になる。空のうちに
  //   初期化すると rankFilter が空 Set で固定され、データ到着後も prev!==null で再初期化されず、
  //   cls の rank フィルタが全 AI 認識物を脱落させる (= グリッド 0 件)。よって allRanks が
  //   非空になった最初の1回でのみ初期化する (空のうちは null = フィルタ無効 のまま据え置く)。
  useEffect(() => {
    setRankFilter((prev) => (prev === null && allRanks.length > 0 ? new Set(allRanks) : prev));
  }, [allRanks]);
  // docs/107 Wave4 / old2 MatrixView L387-422: 行/列/セルの複数選択 (EcwMatrixState 相当)。
  const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set());
  const [checkedCols, setCheckedCols] = useState<Set<string>>(new Set());
  const [checkedCells, setCheckedCells] = useState<Set<string>>(new Set());
  const toggleInSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, code: string) =>
    setter((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code);
      else n.add(code);
      return n;
    });
  // docs/107 Wave4 (remaining): score フィルタ (number 集合) 用 toggle。
  const toggleInNumberSet = (
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    value: number,
  ) =>
    setter((prev) => {
      const n = new Set(prev);
      if (n.has(value)) n.delete(value);
      else n.add(value);
      return n;
    });
  const clearSelection = () => {
    setCheckedRows(new Set());
    setCheckedCols(new Set());
    setCheckedCells(new Set());
  };

  // matrixRules 変化時 (admin の保存反映 / config 切替) は dropdown 既定値を更新 + 選択をクリア
  useEffect(() => {
    setYAxisKey(initialV);
    setXAxisKey(initialH);
    setCheckedRows(new Set());
    setCheckedCols(new Set());
    setCheckedCells(new Set());
  }, [initialV, initialH]);

  // docs/107 Wave4 (remaining): matrixRules 由来の設定既定値が変わったら ⚙設定 state を同期。
  useEffect(() => {
    setImageSize(initialImageSize);
  }, [initialImageSize]);
  useEffect(() => {
    setShowTitle(matrixRules?.showTitle ?? false);
  }, [matrixRules]);
  useEffect(() => {
    setSortField(initialSortField);
    setSortOrder(initialSortOrder);
  }, [initialSortField, initialSortOrder]);
  useEffect(() => {
    setScoreFilter(initialScoreFilter);
  }, [initialScoreFilter]);

  // スコアレス format (aggregateScores の positiveKey/negativeKey 未設定。例: keikan) は
  // すべての分類行の score が 0 になる。既定 scoreFilter [-3,-2,-1,1,2,3] は 0 を含まないため、
  // スコアレス mode で score フィルタを適用すると全行が脱落してしまう。よってスコアありの
  // format (ECW/walker/bousai 等) のときだけ score フィルタを適用する。
  const isScoredFormat = !!(
    outputFormat?.schema?.aggregateScores?.positiveKey ||
    outputFormat?.schema?.aggregateScores?.negativeKey
  );

  // docs/107 Wave4 (remaining) / old2 MatrixView L57-65: スコア・ランクフィルタ。
  // old2 踏襲: cls モードでのみ classification 行を score / rank で絞り込む。
  // 投稿単位モードでは投稿に符号付き score が必ずあるとは限らないため、絞込は適用せず全件。
  // (extraFields.score を持つ post — 主に cls 擬似 post — のみ scoreFilter で間引く)
  const filteredMatrixPosts = useMemo<PostMinimal[]>(() => {
    if (!isClsMode) return matrixPosts;
    return matrixPosts.filter((p) => {
      if (isScoredFormat) {
        const score = readPostScore(p);
        if (score !== null && !scoreFilter.has(score)) return false;
      }
      const rank = readPostRank(p);
      if (rank !== null && rankFilter !== null && !rankFilter.has(rank)) return false;
      return true;
    });
  }, [isClsMode, matrixPosts, scoreFilter, rankFilter, isScoredFormat]);

  // docs/105: rule 軸の group 抽出は bucketMethod (custom-ranges / mean-split /
  // positive-negative / by-name) に応じて分岐 (mean-split は filteredMatrixPosts の平均が必要)。
  const yAxis = useMemo(() => {
    // docs/29 §8-1: 追加の観点の軸は、その観点のカテゴリマスタで分類する。
    const pa = resolvePerspectiveAxis(yAxisKey, perspectives);
    if (pa.perspective) {
      return appendEmptyBucket(
        isDefaultAxisKey(pa.baseKey) ? extractDefaultAxis(pa.perspective.resolver, pa.baseKey) : [],
        includeEmptyByKey.has(yAxisKey),
      );
    }
    const base = isPostFieldAxisKey(yAxisKey)
      ? orientPostFieldAxisGroups(
          postFieldAxisByKey.get(yAxisKey)?.field,
          postFieldAxisByKey.get(yAxisKey)?.groups ?? [],
          true,
        )
      : isDefaultAxisKey(yAxisKey)
        ? extractDefaultAxis(resolver, yAxisKey)
        : (() => {
            const rule = customRuleByKey.get(yAxisKey);
            return rule ? extractRuleAxis(rule, resolver, filteredMatrixPosts, true) : [];
          })();
    return appendEmptyBucket(base, includeEmptyByKey.has(yAxisKey));
  }, [
    perspectives,
    resolver,
    yAxisKey,
    customRuleByKey,
    postFieldAxisByKey,
    filteredMatrixPosts,
    includeEmptyByKey,
  ]);

  const xAxis = useMemo(() => {
    // docs/29 §8-1: 追加の観点の軸は、その観点のカテゴリマスタで分類する。
    const pa = resolvePerspectiveAxis(xAxisKey, perspectives);
    if (pa.perspective) {
      return appendEmptyBucket(
        isDefaultAxisKey(pa.baseKey) ? extractDefaultAxis(pa.perspective.resolver, pa.baseKey) : [],
        includeEmptyByKey.has(xAxisKey),
      );
    }
    const base = isPostFieldAxisKey(xAxisKey)
      ? orientPostFieldAxisGroups(
          postFieldAxisByKey.get(xAxisKey)?.field,
          postFieldAxisByKey.get(xAxisKey)?.groups ?? [],
          false,
        )
      : isDefaultAxisKey(xAxisKey)
        ? extractDefaultAxis(resolver, xAxisKey)
        : (() => {
            const rule = customRuleByKey.get(xAxisKey);
            return rule ? extractRuleAxis(rule, resolver, filteredMatrixPosts, false) : [];
          })();
    return appendEmptyBucket(base, includeEmptyByKey.has(xAxisKey));
  }, [
    perspectives,
    resolver,
    xAxisKey,
    customRuleByKey,
    postFieldAxisByKey,
    filteredMatrixPosts,
    includeEmptyByKey,
  ]);

  const cellPosts = useMemo(() => {
    const map = new Map<string, PostMinimal[]>();
    const yIsDefault = isDefaultAxisKey(yAxisKey);
    const xIsDefault = isDefaultAxisKey(xAxisKey);
    const yPerspective = resolvePerspectiveAxis(yAxisKey, perspectives).perspective;
    const xPerspective = resolvePerspectiveAxis(xAxisKey, perspectives).perspective;
    const yRule =
      !yIsDefault && !isPostFieldAxisKey(yAxisKey) ? (customRuleByKey.get(yAxisKey) ?? null) : null;
    const xRule =
      !xIsDefault && !isPostFieldAxisKey(xAxisKey) ? (customRuleByKey.get(xAxisKey) ?? null) : null;
    const yPostFieldEntry = postFieldAxisByKey.get(yAxisKey) ?? null;
    const xPostFieldEntry = postFieldAxisByKey.get(xAxisKey) ?? null;
    // mean-split は avg に依存するため group (= yAxis/xAxis) を渡して code を解決する。
    const yIsMeanSplit = !!yRule && normalizeBucketMethod(yRule) === 'mean-split';
    const xIsMeanSplit = !!xRule && normalizeBucketMethod(xRule) === 'mean-split';
    const codesFor = (
      post: PostMinimal,
      axisKey: AxisKey,
      rule: AnalysisAxisRule | null,
      isMeanSplit: boolean,
      groups: AxisGroup[],
      postFieldEntry: { field: FieldDef; groups: PostFieldAxisGroup[] } | null,
    ): Set<string> => {
      if (isMeanSplit && rule) return postToMeanSplitCodes(post, rule, groups);
      // docs/29 §8-1: 追加の観点の軸は、その観点の結果に写した投稿で値を出す。
      const pa = resolvePerspectiveAxis(axisKey, perspectives);
      if (pa.perspective) return perspectiveAxisCodes(post, pa.perspective, pa.baseKey);
      return postToCodes(post, axisKey, resolver, rule, outputFormat, postFieldEntry);
    };
    // (2) includeEmpty 軸では、その軸に値を持たない投稿/行を「（該当なし）」バケットへ寄せる。
    const yEmpty = includeEmptyByKey.has(yAxisKey);
    const xEmpty = includeEmptyByKey.has(xAxisKey);
    for (const post of filteredMatrixPosts) {
      // 既定軸 (ECW 系) のみ status=done を要求。custom axis (extraFields / postField) は status と独立。
      if ((yIsDefault || xIsDefault) && post.aiAnalysisStatus !== 'done') continue;
      // 追加の観点の軸は、その観点の解析が済んだ投稿だけ。
      if (yPerspective && !isAnalyzedForPerspective(post, yPerspective)) continue;
      if (xPerspective && !isAnalyzedForPerspective(post, xPerspective)) continue;
      let yCodes = codesFor(post, yAxisKey, yRule, yIsMeanSplit, yAxis, yPostFieldEntry);
      let xCodes = codesFor(post, xAxisKey, xRule, xIsMeanSplit, xAxis, xPostFieldEntry);
      // (2) 投稿単位: 1 投稿 = 1 マス。感情 / 対象コード / ゲール基準のように複数値を持つ軸は
      //     **先頭 (最も当てはまる値)** だけを対象にする。全値を使うと同じ投稿が複数セルに
      //     計上され、セルの件数が実際の投稿数より膨らむため。
      //     AI 認識物単位では上で行に展開済みなので、ここでは絞らない。
      if (!isClsMode) {
        yCodes = firstCodeOnly(yCodes);
        xCodes = firstCodeOnly(xCodes);
      }
      if (yCodes.size === 0 && yEmpty) yCodes = new Set([EMPTY_AXIS_CODE]);
      if (xCodes.size === 0 && xEmpty) xCodes = new Set([EMPTY_AXIS_CODE]);
      for (const y of yCodes) {
        for (const x of xCodes) {
          const key = `${y}:${x}`;
          const arr = map.get(key);
          if (arr) arr.push(post);
          else map.set(key, [post]);
        }
      }
    }
    // docs/107 Wave4 (remaining) / old2 セル内並び順: 各セル内 post を sortField/sortOrder で並べ替え。
    for (const [key, arr] of map) {
      map.set(key, sortCellPosts(arr, sortField, sortOrder));
    }
    return map;
  }, [
    perspectives,
    filteredMatrixPosts,
    resolver,
    yAxisKey,
    xAxisKey,
    customRuleByKey,
    outputFormat,
    postFieldAxisByKey,
    yAxis,
    xAxis,
    sortField,
    sortOrder,
    includeEmptyByKey,
    isClsMode,
  ]);

  function swapAxes() {
    setYAxisKey(xAxisKey);
    setXAxisKey(yAxisKey);
  }

  // docs/107 Wave4 / old2 MatrixView L387-422,444-477: セルが選択状態か (行 or 列 or セル選択)。
  const isCellSelected = (yCode: string, xCode: string): boolean =>
    checkedRows.has(yCode) || checkedCols.has(xCode) || checkedCells.has(`${yCode}:${xCode}`);

  // docs/107 Wave4: 選択中の全セルの投稿を OR 集約 (dedup) → header「📊 分析」一括 handoff の対象。
  const selectedPostIds = useMemo(() => {
    const set = new Set<string>();
    for (const y of yAxis) {
      for (const x of xAxis) {
        if (
          checkedRows.has(y.code) ||
          checkedCols.has(x.code) ||
          checkedCells.has(`${y.code}:${x.code}`)
        ) {
          for (const p of cellPosts.get(`${y.code}:${x.code}`) ?? []) set.add(p.id);
        }
      }
    }
    return [...set];
  }, [yAxis, xAxis, cellPosts, checkedRows, checkedCols, checkedCells]);
  const hasSelection = checkedRows.size + checkedCols.size + checkedCells.size > 0;

  if (resolver.loading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-500">
        マスターを読込中…
      </div>
    );
  }

  // 軸の値が空 (例: 選んだ軸を持つ投稿が無い) でも、ヘッダ・データソース切替・軸セレクタは
  // 常に描画し続ける (空状態で UI 全体が消えて「軸を変えて元に戻せない」状態を防ぐ)。
  // 空メッセージはグリッド領域にのみ表示する (下部の table を分岐)。
  const axisEmpty = yAxis.length === 0 || xAxis.length === 0;

  // Sprint AW fixup (reviewer 🔴 指摘): 両軸 custom の場合 aiAnalysisStatus 不問で集計するため、
  // 「解析済 N 件」表示が乖離する。両軸 custom 時は「分析対象 N 件」に切替。
  //
  // Sprint BG Part 4 reviewer follow-up: 両軸 custom 時は totalAnalyzed === posts.length が常に成立し
  // 「分析対象 N 件 / 全 N 件」が冗長 (2 つ同じ数字)。両軸 custom 時は countLabel 自体を null にして、
  // header span 内で「全 N 件」のみ表示する (UI 簡略化)。
  const yIsDefaultAxis = isDefaultAxisKey(yAxisKey);
  const xIsDefaultAxis = isDefaultAxisKey(xAxisKey);
  // Task C: postField 軸 (dataSettings 由来) は extraFields 経由なので AI 完了不要。
  // docs/105: cls モードでは擬似 post を全件集計 (status=done を立てているため解析済 = 全件)。
  const requiresAiAnalysis = (yIsDefaultAxis || xIsDefaultAxis) && !isClsMode;
  // docs/107 Wave4 (remaining): cls モードは score/rank フィルタ後の件数を母数にする。
  const totalAnalyzed = requiresAiAnalysis
    ? filteredMatrixPosts.filter((p) => p.aiAnalysisStatus === 'done').length
    : filteredMatrixPosts.length;
  const countLabel = requiresAiAnalysis ? '解析済' : null;
  // 件数表示の母数 (post: 投稿数 / cls: AI 認識物数)。
  const totalLabel = isClsMode ? 'AI認識物' : '全';

  return (
    <div className="p-2 sm:p-4 overflow-auto h-full">
      <div className="mb-2 sm:mb-3 flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-800 min-w-0 truncate">
          {labelOf(yAxisKey)} × {labelOf(xAxisKey)}
        </h2>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* スマホ: 軸/データソース行の開閉トグル (sm+ では常時展開のため非表示)。 */}
          <button
            type="button"
            onClick={() => setAxisBarOpen((v) => !v)}
            aria-expanded={axisBarOpen}
            className={`sm:hidden text-xs px-2.5 py-1 rounded font-semibold border min-h-touch ${
              axisBarOpen
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-slate-600 border-slate-300'
            }`}
          >
            🎛 軸{axisBarOpen ? ' ▲' : ' ▼'}
          </button>
          {/* docs/107 Wave4 / old2 MatrixView L426-477: 選択セルの投稿を一括で分析へ。 */}
          {onAnalyzeCell && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={selectedPostIds.length === 0}
                onClick={() =>
                  onAnalyzeCell(selectedPostIds, {
                    vAxisKey: yAxisKey,
                    hAxisKey: xAxisKey,
                    dataSource: isClsMode ? 'cls' : 'post',
                  })
                }
                /* 要件6: 分析ボタンは目立つ色に (old2 は選択時に赤+pulse でアクセント)。v2 では
                   アクセント色 (accent) + shadow/ring で強調し、未選択時は slate で抑制する。 */
                className="text-xs px-3 py-1.5 rounded-md font-bold text-white shadow-md ring-2 ring-accent-600/40 bg-accent-600 hover:bg-accent-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed min-h-touch"
              >
                📊 分析 ({selectedPostIds.length})
              </button>
              {hasSelection && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-[11px] text-slate-500 hover:text-slate-700 underline"
                >
                  選択クリア
                </button>
              )}
            </div>
          )}
          {/* docs/107 Wave4 (remaining) / old2 MatrixView L506-637: ⚙ 設定 popover。
              画像サイズ / タイトル表示 / スコア連動サイズ / 軸大型表示 / セル内並び順 /
              スコア・ランクフィルタをまとめる。 */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setSettingsOpen((v) => !v)}
              aria-expanded={settingsOpen}
              aria-haspopup="dialog"
              className={`text-xs px-2.5 py-1 rounded font-semibold border min-h-touch ${
                settingsOpen
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              ⚙ 設定
            </button>
            {settingsOpen && (
              <>
                {/* クリックアウトで閉じる overlay (old2 L654 踏襲) */}
                <button
                  type="button"
                  aria-label="設定を閉じる"
                  onClick={() => setSettingsOpen(false)}
                  className="fixed inset-0 z-40 bg-transparent border-none cursor-default"
                />
                <div
                  role="dialog"
                  aria-label="マトリクス設定"
                  className="absolute right-0 top-full mt-1 z-50 w-72 max-w-[90vw] max-h-[70vh] overflow-y-auto bg-white rounded-md shadow-xl border border-slate-200 p-3 flex flex-col gap-3 text-left"
                >
                  <div className="text-xs font-bold text-primary-700">マトリクス設定</div>

                  {/* 画像サイズ (old2 L658-668) */}
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <span aria-hidden>📷</span>
                    <span className="shrink-0">画像サイズ</span>
                    <input
                      type="range"
                      min={12}
                      max={96}
                      step={4}
                      value={imageSize}
                      onChange={(e) => setImageSize(Number(e.target.value))}
                      className="flex-1 cursor-pointer"
                      aria-label="セル内サムネイルの画像サイズ"
                    />
                    <span className="w-8 text-right tabular-nums">{imageSize}</span>
                  </label>

                  {/* 全セルの写真をすべて表示 (要件2): 各セルの表示上限を撤廃して全サムネを並べる。
                      写真の数で件数を視覚的に把握したい用途向け。 */}
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={expandAllCells}
                      onChange={(e) => setExpandAllCells(e.target.checked)}
                    />
                    全セルの写真をすべて表示
                  </label>

                  {/* タイトル表示 (old2 L668-677) */}
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTitle}
                      onChange={(e) => setShowTitle(e.target.checked)}
                    />
                    投稿タイトルを併記
                  </label>

                  {/* スコア連動サイズ (old2 L577-580) */}
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scoreSizing}
                      onChange={(e) => setScoreSizing(e.target.checked)}
                    />
                    スコア連動サイズ (|score| で拡縮)
                  </label>

                  {/* 縦横軸大型表示 (old2 axisDisplayName 大型表示 L680,702) */}
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={largeAxis}
                      onChange={(e) => setLargeAxis(e.target.checked)}
                    />
                    縦横軸を大型表示
                  </label>

                  {/* セル内並び順 (old2 L546-560) */}
                  <div className="flex flex-col gap-1 bg-slate-50 rounded p-2">
                    <div className="text-[11px] font-semibold text-slate-600">セル内の並び順</div>
                    <div className="flex items-center gap-2">
                      <select
                        value={sortField}
                        onChange={(e) => setSortField(e.target.value as MatrixSortField)}
                        className="border border-slate-300 rounded px-2 py-1 text-xs"
                        aria-label="セル内の並び順 (項目)"
                      >
                        <option value="score">スコア</option>
                        <option value="rank">ランク</option>
                        <option value="date">日時</option>
                      </select>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as MatrixSortOrder)}
                        className="border border-slate-300 rounded px-2 py-1 text-xs"
                        aria-label="セル内の並び順 (方向)"
                      >
                        <option value="desc">降順</option>
                        <option value="asc">昇順</option>
                      </select>
                    </div>
                  </div>

                  {/* スコアフィルタ (old2 L562-581) */}
                  <div className="flex flex-col gap-1 bg-slate-50 rounded p-2">
                    <div className="text-[11px] font-semibold text-slate-600">
                      表示するスコア
                      {!isClsMode && (
                        <span className="ml-1 font-normal text-slate-400">
                          (スコア付き要素のみ)
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {DEFAULT_SCORE_FILTER.map((s) => {
                        const on = scoreFilter.has(s);
                        const pos = s > 0;
                        return (
                          <label
                            key={s}
                            className={`text-[11px] cursor-pointer px-2 py-0.5 rounded-full font-semibold border ${
                              on
                                ? pos
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                  : 'bg-rose-100 text-rose-800 border-rose-300'
                                : 'bg-white text-slate-400 border-slate-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleInNumberSet(setScoreFilter, s)}
                              className="sr-only"
                              aria-label={`スコア ${pos ? '+' : ''}${s} を表示`}
                            />
                            {pos ? '+' : ''}
                            {s}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* ランクフィルタ (cls モードのみ、old2 L583-600) */}
                  {isClsMode && allRanks.length > 0 && (
                    <div className="flex flex-col gap-1 bg-amber-50 rounded p-2">
                      <div className="text-[11px] font-semibold text-slate-600">表示するランク</div>
                      <div className="flex flex-wrap gap-1">
                        {allRanks.map((r) => {
                          const on = rankFilter?.has(r) ?? false;
                          return (
                            <label
                              key={r}
                              className={`text-[11px] cursor-pointer px-2 py-0.5 rounded-full font-semibold border ${
                                on
                                  ? 'bg-amber-500 text-white border-amber-500'
                                  : 'bg-white text-slate-400 border-slate-200'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() =>
                                  setRankFilter((prev) => {
                                    const base = prev ?? new Set(allRanks);
                                    const n = new Set(base);
                                    if (n.has(r)) n.delete(r);
                                    else n.add(r);
                                    return n;
                                  })
                                }
                                className="sr-only"
                                aria-label={`ランク ${r} を表示`}
                              />
                              {r}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <span className="text-xs text-slate-500">
            {countLabel ? `${countLabel} ${totalAnalyzed} 件 / ` : ''}
            {totalLabel} {filteredMatrixPosts.length} 件
          </span>
        </div>
      </div>

      {/* 要件5: 「データソース」切替と「縦軸 / 横軸」選択を 1 段の設定行にまとめる
          (docs/105: 投稿単位 ⇔ AI 認識物単位。classifications が 0 件なら切替は出さない)。
          スマホでは axisBarOpen で開閉 (既定畳み)、sm+ では常時展開。 */}
      <div
        className={`${axisBarOpen ? 'flex' : 'hidden'} sm:flex mb-3 sm:mb-4 flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-2 bg-slate-50 p-2 sm:p-3 rounded-md`}
      >
        {hasClassifications && (
          <div className="flex items-center gap-2" role="radiogroup" aria-label="データソース">
            <span className="text-xs font-semibold text-slate-600">データソース</span>
            <button
              type="button"
              role="radio"
              aria-checked={dataSource === 'post'}
              onClick={() => setDataSource('post')}
              className={`text-xs px-3 py-1 rounded-full font-semibold min-h-touch ${
                dataSource === 'post'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-300'
              }`}
            >
              投稿単位
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={dataSource === 'cls'}
              onClick={() => setDataSource('cls')}
              className={`text-xs px-3 py-1 rounded-full font-semibold min-h-touch ${
                dataSource === 'cls'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-300'
              }`}
            >
              {/* 表示軸で列挙値を展開した後の件数 = 実際にマトリクスへ並ぶデータ点数。
                  ヘッダの母数表示と数が食い違わないよう展開後で数える。 */}
              AI認識物 ({expandedClsRows.length})
            </button>
            {/* 軸選択との区切り (wide 時のみ) */}
            <span className="hidden sm:block self-stretch border-l border-slate-200 ml-1" />
          </div>
        )}
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <span className="shrink-0">縦軸</span>
          <select
            value={yAxisKey}
            onChange={(e) => setYAxisKey(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1 text-xs min-h-touch max-w-[40vw] sm:max-w-none"
            aria-label="縦軸"
          >
            {axisOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={swapAxes}
          aria-label="縦軸と横軸を入れ替え"
          className="text-xs text-primary-600 hover:text-primary-700 underline bg-transparent border-none cursor-pointer min-h-touch min-w-touch"
        >
          ⇄ 入替
        </button>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <span className="shrink-0">横軸</span>
          <select
            value={xAxisKey}
            onChange={(e) => setXAxisKey(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1 text-xs min-h-touch max-w-[40vw] sm:max-w-none"
            aria-label="横軸"
          >
            {axisOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {axisEmpty ? (
        <div className="flex flex-col items-center justify-center text-sm text-slate-500 gap-2 px-4 py-12 text-center border border-dashed border-slate-200 rounded-md">
          <p>
            選んだ軸（{labelOf(yAxisKey)} × {labelOf(xAxisKey)}）に該当する
            {isClsMode ? 'AI認識物' : '投稿'}がありません。
          </p>
          <p className="text-xs text-slate-400">
            上の「縦軸 / 横軸」セレクタやデータソース切替で別の軸をお試しください
            （景観調査では「景観の観点」「資源/課題」などが選べます）。
          </p>
        </div>
      ) : (
        <div className="inline-block min-w-full">
          <table className="border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white p-2 text-[10px] text-slate-400 align-bottom text-right border-b border-slate-200">
                  <span className="italic">↓ 縦 \ 横 →</span>
                </th>
                {xAxis.map((x) => (
                  <th
                    key={x.code}
                    className="p-2 text-xs font-medium align-bottom whitespace-nowrap border-b border-slate-200"
                  >
                    <div className="flex flex-col items-center gap-1">
                      {/* docs/107 Wave4 (remaining): 縦横軸大型表示 ON で chip フォントを拡大。 */}
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-white ${
                          largeAxis ? 'text-sm font-semibold' : 'text-[10px]'
                        }`}
                        style={{ backgroundColor: x.color, color: contrastTextColor(x.color) }}
                      >
                        {x.label}
                      </span>
                      {/* docs/107 Wave4 / old2 MatrixView L712-743: 列まるごと選択。 */}
                      <input
                        type="checkbox"
                        checked={checkedCols.has(x.code)}
                        onChange={() => toggleInSet(setCheckedCols, x.code)}
                        aria-label={`列「${x.label}」を選択`}
                        className="w-3.5 h-3.5"
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {yAxis.map((y) => (
                <tr key={y.code}>
                  <th className="sticky left-0 bg-white p-2 text-xs font-medium text-right border-r border-slate-200 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5">
                      {/* docs/107 Wave4 / old2 MatrixView L712-743: 行まるごと選択。 */}
                      <input
                        type="checkbox"
                        checked={checkedRows.has(y.code)}
                        onChange={() => toggleInSet(setCheckedRows, y.code)}
                        aria-label={`行「${y.label}」を選択`}
                        className="w-3.5 h-3.5"
                      />
                      {/* docs/107 Wave4 (remaining): 縦横軸大型表示 ON で chip フォントを拡大。 */}
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-white ${
                          largeAxis ? 'text-sm font-semibold' : 'text-[10px]'
                        }`}
                        style={{ backgroundColor: y.color, color: contrastTextColor(y.color) }}
                      >
                        {y.label}
                      </span>
                    </div>
                  </th>
                  {xAxis.map((x) => {
                    const key = `${y.code}:${x.code}`;
                    const cellList = cellPosts.get(key) ?? [];
                    const count = cellList.length;
                    // docs/107 Wave4 / old2 MatrixView L757-833: セルは写真サムネイル格子。
                    // 大量セル対策に既定の表示上限 (SHOWN) を設けるが、
                    //  - 要件1: 「+N」をタップしたセル (expandedCells) は全件表示
                    //  - 要件2: 「全セルの写真をすべて表示」(expandAllCells) が ON なら全セル全件表示
                    const SHOWN = 24;
                    const showAll = expandAllCells || expandedCells.has(key);
                    const shown = showAll ? cellList : cellList.slice(0, SHOWN);
                    const hiddenCount = count - shown.length;
                    const cellSelected = isCellSelected(y.code, x.code);
                    return (
                      <td
                        key={key}
                        /* old2 踏襲: チェックボックスではなくセル自体のクリックで選択をトグルする。
                         写真サムネ / 「+N」等の子要素は stopPropagation して詳細表示・展開を優先。 */
                        onClick={count > 0 ? () => toggleInSet(setCheckedCells, key) : undefined}
                        onKeyDown={
                          count > 0
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleInSet(setCheckedCells, key);
                                }
                              }
                            : undefined
                        }
                        role={count > 0 ? 'button' : undefined}
                        tabIndex={count > 0 ? 0 : undefined}
                        aria-pressed={count > 0 ? cellSelected : undefined}
                        className={`p-1 border border-slate-100 align-top min-w-[110px] ${
                          count > 0 ? 'cursor-pointer' : ''
                        } ${
                          cellSelected
                            ? 'bg-primary-50 ring-1 ring-primary-300 ring-inset'
                            : count > 0
                              ? 'hover:bg-slate-50'
                              : ''
                        }`}
                        aria-label={count > 0 ? `${y.label} × ${x.label}: ${count} 件` : undefined}
                      >
                        {count === 0 ? (
                          <div className="text-center text-[10px] text-slate-300 p-2">—</div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {/* 要件3: 写真は左上から左揃え (justify-start) で並べ、件数を写真の数で
                              視覚的に把握できるようにする (横棒グラフ状の並び)。 */}
                            <div className="flex flex-wrap gap-0.5 justify-start items-start">
                              {shown.map((p) => (
                                <MatrixThumb
                                  key={p.id}
                                  post={p}
                                  size={scoreSizing ? scoreSizedThumb(imageSize, p) : imageSize}
                                  showTitle={showTitle}
                                  onClick={() => onSelectPost(p)}
                                />
                              ))}
                              {/* 要件1: 非表示分の「+N」をタップすると、このセルだけ全件表示に展開する。
                                セル選択のトグルとは独立させるため stopPropagation。 */}
                              {hiddenCount > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleInSet(setExpandedCells, key);
                                  }}
                                  aria-label={`このセルの残り ${hiddenCount} 件をすべて表示`}
                                  className="self-center text-[10px] font-semibold text-primary-600 hover:text-primary-700 underline px-1 py-0.5"
                                >
                                  +{hiddenCount}
                                </button>
                              )}
                              {/* 個別展開中 (全件表示モードでない) は折りたたみ操作を出す。 */}
                              {!expandAllCells && expandedCells.has(key) && count > SHOWN && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleInSet(setExpandedCells, key);
                                  }}
                                  aria-label="このセルの表示を折りたたむ"
                                  className="self-center text-[10px] text-slate-400 hover:text-slate-600 underline px-1 py-0.5"
                                >
                                  閉じる
                                </button>
                              )}
                            </div>
                            {/* 件数 + 選択状態の表示 (クリックはセル全体で受ける)。 */}
                            <div className="flex items-center gap-1 px-0.5">
                              <span className="text-[10px] text-slate-400">{count}件</span>
                              {cellSelected && (
                                <span className="text-[10px] font-semibold text-primary-600">
                                  ✓ 選択中
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * マトリクスセル内の写真サムネイル (docs/107 Wave4 / old2 MatrixView L757-833)。
 * photoThumbPath/Url を usePhotoUrl で解決し img 表示。写真が無い投稿は 📷 プレースホルダ
 * (old2 DUMMY_IMAGE_URL カメラアイコン相当)。クリックで投稿詳細 (onSelectPost)。
 * docs/107 Wave4 (remaining): showTitle ON 時はサムネ下に投稿タイトルを併記 (old2 L780-784,809-813)。
 */
function MatrixThumb({
  post,
  size,
  showTitle = false,
  onClick,
}: {
  post: PostMinimal;
  size: number;
  showTitle?: boolean;
  onClick: () => void;
}) {
  // docs/106: doc 保存済 thumb URL を渡して cache miss 時の楽観表示 + オフライン fallback。
  const photo = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl ?? post.photoUrl);
  const style = { width: `${size}px`, height: `${size}px` } as React.CSSProperties;
  // 写真クリックは「投稿詳細を開く」操作。セル全体クリック (選択トグル) には伝播させない。
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };
  const ring =
    'shrink-0 rounded overflow-hidden border border-slate-200 hover:ring-2 hover:ring-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500';
  const thumb =
    photo.status === 'ready' && photo.url ? (
      <button
        type="button"
        onClick={handleClick}
        title={post.title || ''}
        className={ring}
        style={style}
      >
        <img
          src={photo.url}
          alt={post.title || '投稿写真'}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </button>
    ) : (
      <button
        type="button"
        onClick={handleClick}
        title={post.title || ''}
        className={`${ring} bg-slate-100 flex items-center justify-center text-slate-400 ${
          photo.status === 'loading' ? 'animate-pulse' : ''
        }`}
        style={style}
      >
        <span style={{ fontSize: `${Math.max(12, Math.round(size * 0.4))}px` }}>📷</span>
      </button>
    );
  if (!showTitle) return thumb;
  return (
    <span className="inline-flex flex-col items-center" style={{ width: `${size}px` }}>
      {thumb}
      <span className="mt-0.5 text-[9px] leading-tight text-slate-500 text-center break-words line-clamp-2">
        {post.title || ''}
      </span>
    </span>
  );
}
